import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserProfile, CaregiverRole } from '@/types';

interface UserStoreState {
  userId: string | null;
  email: string | null;
  displayName: string | null;
  avatarEmoji: string | null;
  avatarColor: string | null;
  /** Storage path for the uploaded profile image (null = use emoji fallback). */
  avatarImagePath: string | null;
  /**
   * Free-form family role (e.g. "Mom", "Dad") in the ACTIVE household —
   * `household_members.family_role`, published with the membership. It used
   * to mirror `users.role`, one value for every household (YAR-485).
   */
  profileRole: string | null;
  timezone: string | null;
  onboardingCompletedAt: string | null;
  /**
   * users.onboarding_step — where the admin onboarding was left, per account
   * (YAR-438). Read once by OnboardingEntry to seed a device that has no
   * local copy of the flow; the onboarding store owns it from then on.
   */
  onboardingStep: string | null;
  approved: boolean;
  /** household_members.role — access level, not the profile relationship. */
  role: CaregiverRole | null;
  roleResolved: boolean;
}

interface UserStoreActions {
  setUser: (profile: UserProfile) => void;
  setRole: (role: CaregiverRole | null) => void;
  updateProfile: (
    partial: Partial<
      Pick<
        UserStoreState,
        | 'displayName'
        | 'avatarEmoji'
        | 'avatarColor'
        | 'avatarImagePath'
        | 'profileRole'
        | 'timezone'
        | 'onboardingCompletedAt'
        | 'role'
      >
    >,
  ) => void;
  clearUser: () => void;
}

const initialState: UserStoreState = {
  userId: null,
  email: null,
  displayName: null,
  avatarEmoji: null,
  avatarColor: null,
  avatarImagePath: null,
  profileRole: null,
  timezone: null,
  onboardingCompletedAt: null,
  onboardingStep: null,
  approved: false,
  role: null,
  roleResolved: false,
};

export const useUserStore = create<UserStoreState & UserStoreActions>()(
  persist(
    (set) => ({
      ...initialState,
      setUser: (profile) =>
        set({
          userId: profile.id,
          email: profile.email,
          displayName: profile.display_name,
          avatarEmoji: profile.avatar_emoji,
          avatarColor: profile.avatar_color,
          avatarImagePath: profile.avatar_image_path,
          // profileRole is NOT read from users.role any more: it is per
          // household and arrives with the membership (applyMembership).
          timezone: profile.timezone,
          onboardingCompletedAt: profile.onboarding_completed_at,
          // `?? null`: a row read through a bundle that predates the column
          // (web and edge do not deploy atomically) has no key at all.
          onboardingStep: profile.onboarding_step ?? null,
          approved: profile.approved,
          // supabase_id, created_at, updated_at are intentionally excluded — not needed by UI consumers
        }),
      setRole: (role) => set({ role, roleResolved: true }),
      updateProfile: (partial) =>
        set({
          ...(partial.displayName !== undefined && {
            displayName: partial.displayName,
          }),
          ...(partial.avatarEmoji !== undefined && {
            avatarEmoji: partial.avatarEmoji,
          }),
          ...(partial.avatarColor !== undefined && {
            avatarColor: partial.avatarColor,
          }),
          ...(partial.avatarImagePath !== undefined && {
            avatarImagePath: partial.avatarImagePath,
          }),
          ...(partial.profileRole !== undefined && {
            profileRole: partial.profileRole,
          }),
          ...(partial.timezone !== undefined && { timezone: partial.timezone }),
          ...(partial.onboardingCompletedAt !== undefined && {
            onboardingCompletedAt: partial.onboardingCompletedAt,
          }),
          ...(partial.role !== undefined && { role: partial.role }),
        }),
      clearUser: () => set(initialState),
    }),
    {
      name: 'yaruta_user',
    },
  ),
);
